import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import type {
  NoticeBlock,
  NoticeCatalog,
  NoticeColumn,
  NoticeContextList,
} from './notice-fidelity.models';
import {
  noticeStructureListFields,
  type NoticeStructureCommand,
} from './notice-structure-commands';

type ColumnTextProperty = 'label' | 'field' | 'format';
type FocusRequest =
  | { kind: 'add'; previousIds: readonly string[] }
  | { kind: 'column'; columnId: string; announcement: string };

/** Emits bounded editing intentions; the source owner applies each to its current revision. */
@Component({
  selector: 'phms-notice-table-properties',
  standalone: true,
  imports: [CommonModule, FormsModule, UiButtonDirective, UiControlDirective],
  templateUrl: './notice-table-properties.component.html',
  styleUrl: './notice-table-properties.component.scss',
})
export class NoticeTablePropertiesComponent implements OnChanges, OnDestroy {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  @Input({ required: true }) block!: NoticeBlock;
  @Input({ required: true }) catalog!: NoticeCatalog;
  @Input() contextEnabled = false;
  @Input() disabled = false;
  @Output() readonly command = new EventEmitter<NoticeStructureCommand>();
  readonly unavailableEmptyFormat = Object.freeze({ unavailable: true });
  announcement = '';
  validationError = '';
  private focusTimer?: ReturnType<typeof setTimeout>;
  private destroyed = false;

  get columns(): readonly NoticeColumn[] {
    return this.block.columns ?? [];
  }

  get lists(): readonly NoticeContextList[] {
    return (this.catalog.documentContext?.lists ?? []).filter(
      (list) => noticeStructureListFields(list).length >= 1,
    );
  }

  get selectedList(): NoticeContextList | undefined {
    return this.lists.find((list) => list.code === this.block.source);
  }

  get fields(): readonly { code: string; label: string }[] {
    if (!this.contextEnabled)
      return [
        { code: 'label', label: 'Item' },
        { code: 'value', label: 'Value' },
      ];
    return this.selectedList
      ? noticeStructureListFields(this.selectedList)
      : [];
  }

  get canAddColumn(): boolean {
    return !this.disabled && this.columns.length < 4 && this.fields.length > 0;
  }

  knownField(column: NoticeColumn): boolean {
    return this.fields.some((field) => field.code === column.field);
  }

  formats(column: NoticeColumn): readonly string[] {
    if (!this.contextEnabled || !this.selectedList) return [];
    return (
      noticeStructureListFields(this.selectedList).find(
        (field) => field.code === column.field,
      )?.formats ?? []
    );
  }

  knownFormat(column: NoticeColumn): boolean {
    const formats = this.formats(column);
    return (
      formats.length > 0 &&
      (column.format === undefined || formats.includes(column.format))
    );
  }

  formatSelectionValue(
    column: NoticeColumn,
  ): string | Readonly<{ unavailable: boolean }> {
    return column.format === ''
      ? this.unavailableEmptyFormat
      : (column.format ?? '');
  }

  changeList(source: string): void {
    if (
      this.disabled ||
      !this.contextEnabled ||
      source === this.block.source ||
      !this.lists.some((list) => list.code === source)
    )
      return;
    this.command.emit({ type: 'list', blockId: this.block.id, source });
  }

  changeText(
    column: NoticeColumn,
    property: ColumnTextProperty,
    value: string,
  ): void {
    const current = this.currentColumn(column.id);
    if (this.disabled || !current || typeof value !== 'string') return;
    if (
      property === 'field' &&
      !this.fields.some((field) => field.code === value)
    )
      return;
    if (
      property === 'format' &&
      (!this.contextEnabled ||
        !this.formats(current).length ||
        (value !== '' && !this.formats(current).includes(value)))
    )
      return;
    this.command.emit({
      type: 'column-edit',
      blockId: this.block.id,
      columnId: column.id,
      property,
      value,
    });
  }

  changeWidth(column: NoticeColumn, value: number): void {
    if (this.disabled || !this.currentColumn(column.id)) return;
    if (!Number.isFinite(value) || value < 1 || value > 4) {
      this.validationError =
        'Width must be a finite value from 1 to 4; change not applied.';
      return;
    }
    this.validationError = '';
    this.command.emit({
      type: 'column-edit',
      blockId: this.block.id,
      columnId: column.id,
      property: 'width',
      value,
    });
  }

  addColumn(): void {
    if (!this.canAddColumn) return;
    const before = this.block;
    const previousIds = this.columns.map((column) => column.id);
    this.command.emit({ type: 'column-add', blockId: this.block.id });
    this.restoreFocus(before, { kind: 'add', previousIds });
  }

  removeColumn(column: NoticeColumn): void {
    const index = this.columns.findIndex((item) => item.id === column.id);
    if (this.disabled || index < 0 || this.columns.length <= 2) return;
    const before = this.block;
    const next = this.columns[index + 1] ?? this.columns[index - 1];
    this.command.emit({
      type: 'column-remove',
      blockId: this.block.id,
      columnId: column.id,
    });
    this.restoreFocus(before, {
      kind: 'column',
      columnId: next.id,
      announcement: `Removed column ${index + 1}.`,
    });
  }

  moveColumn(column: NoticeColumn, direction: -1 | 1): void {
    const index = this.columns.findIndex((item) => item.id === column.id);
    const target = index + direction;
    if (
      this.disabled ||
      index < 0 ||
      target < 0 ||
      target >= this.columns.length
    )
      return;
    const before = this.block;
    this.command.emit({
      type: 'column-move',
      blockId: this.block.id,
      columnId: column.id,
      direction,
    });
    this.restoreFocus(before, {
      kind: 'column',
      columnId: column.id,
      announcement: `Column moved to position ${target + 1} of ${this.columns.length}.`,
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['block'] ||
      changes['catalog'] ||
      changes['contextEnabled'] ||
      changes['disabled']
    )
      this.validationError = '';
    if (this.disabled) {
      clearTimeout(this.focusTimer);
      this.announcement = '';
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    clearTimeout(this.focusTimer);
  }

  private currentColumn(id: string): NoticeColumn | undefined {
    return this.columns.find((column) => column.id === id);
  }

  private restoreFocus(before: NoticeBlock, request: FocusRequest): void {
    clearTimeout(this.focusTimer);
    // Angular rebinds only an accepted immutable revision. A rejected command must
    // leave focus and the protected source alone; it cannot create a local table.
    this.focusTimer = setTimeout(() => {
      if (
        this.destroyed ||
        this.disabled ||
        this.block === before ||
        this.block.id !== before.id ||
        this.block.kind !== 'repeat'
      )
        return;
      const column =
        request.kind === 'add'
          ? this.columns.find((item) => !request.previousIds.includes(item.id))
          : this.currentColumn(request.columnId);
      if (!column) return;
      const row = Array.from(
        this.host.nativeElement.querySelectorAll<HTMLElement>(
          '[data-column-id]',
        ),
      ).find((element) => element.dataset['columnId'] === column.id);
      row
        ?.querySelector<HTMLInputElement>('[data-column-control="heading"]')
        ?.focus();
      this.announcement =
        request.kind === 'add'
          ? `Added column ${this.columns.findIndex((item) => item.id === column.id) + 1} of ${this.columns.length}.`
          : request.announcement;
    }, 0);
  }
}
